import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchPostByCid } from "./api";
import { Typography, Spin, Button, Space, message, Tooltip, Avatar, List as AntList, Input, Button as AntButton } from "antd";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { verifyMessage } from "ethers";
import LazyLoad from 'react-lazyload';

const { Title, Text } = Typography;

function extractSignature(content) {
  // 提取 <!-- signature: ... -->
  const match = content.match(/<!-- signature: (0x[0-9a-fA-F]+) -->/);
  return match ? match[1] : null;
}

function stripSignature(content) {
  // 去除 HTML 注释签名部分
  return content.replace(/<!-- signature: 0x[0-9a-fA-F]+ -->/, "");
}

function PostDetail() {
  const { cid } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    return document.body.getAttribute('data-color-mode') === 'dark';
  });
  const [signatureValid, setSignatureValid] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [signerAddress, setSignerAddress] = useState("");
  const [comments, setComments] = useState([]);
  const [commentValue, setCommentValue] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    setDarkMode(document.body.getAttribute('data-color-mode') === 'dark');
    const observer = new MutationObserver(() => {
      setDarkMode(document.body.getAttribute('data-color-mode') === 'dark');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-color-mode'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const p = await fetchPostByCid(cid);
        setPost(p);
      } catch (e) {
        message.error("加载博客失败: " + e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [cid]);

  async function verifySignature() {
    if (!post) return;
    setVerifying(true);
    try {
      const sig = extractSignature(post.content || "");
      if (!sig) {
        setSignatureValid(false);
        setSignerAddress("");
        return;
      }
      const msg = (post.title || "") + "\n" + stripSignature(post.content || "");
      const address = verifyMessage(msg, sig);
      setSignatureValid(true);
      setSignerAddress(address);
    } catch (e) {
      setSignatureValid(false);
      setSignerAddress("");
      message.error("签名验证失败: " + (e.message || e));
    } finally {
      setVerifying(false);
    }
  }

  useEffect(() => {
    // 自动验证
    if (post && extractSignature(post.content || "")) {
      verifySignature();
    }
  }, [post]);

  useEffect(() => {
    // 模拟加载评论（实际可接后端）
    setComments([
      { author: '匿名', content: '很棒的内容！', datetime: '1分钟前' },
      { author: '链上用户', content: 'Web3 未来可期', datetime: '5分钟前' },
    ]);
  }, []);

  useEffect(() => {
    // 模拟加载点赞数
    setLikes(2 + Math.floor(Math.random() * 10));
  }, []);

  // 评论提交
  const handleComment = async () => {
    if (!commentValue.trim()) {
      message.error('评论内容不能为空');
      return;
    }
    setCommentLoading(true);
    setTimeout(() => {
      setComments(prev => [
        { author: '你', content: commentValue, datetime: '刚刚' },
        ...prev
      ]);
      setCommentValue('');
      setCommentLoading(false);
      message.success('评论成功');
    }, 600);
  };

  // 点赞功能
  const handleLike = () => {
    if (liked) {
      message.info('你已点赞过');
      return;
    }
    setLiked(true);
    setLikes(prev => prev + 1);
    message.success('点赞成功');
  };

  // 用自定义简单评论组件替代 antd 的 Comment
  function SimpleComment({ author, avatar, content, datetime }) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}>
        {avatar}
        <div style={{ marginLeft: 12, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{author}</div>
          <div style={{ color: '#888', fontSize: 13, marginBottom: 2 }}>{datetime}</div>
          <div style={{ fontSize: 15 }}>{content}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 700, margin: '32px auto', background: darkMode ? '#23232b' : '#fff', borderRadius: 12, boxShadow: darkMode ? '0 2px 12px #1116' : '0 2px 12px #e5e5e5', padding: 32 }}>
      {loading ? (
        <Spin tip="加载中..." />
      ) : post ? (
        <>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 20 }}>{post.title}</Title>
          <div style={{ color: '#888', fontSize: 14, textAlign: 'center', marginBottom: 16 }}>
            {post.timestamp ? new Date(post.timestamp).toLocaleString() : ''}
          </div>
          <LazyLoad height={120} offset={150} once>
            <MarkdownPreview source={stripSignature(post.content || "")} style={{ background: 'none', color: darkMode ? '#eee' : '#222', fontSize: 17, minHeight: 120, marginBottom: 40 }} />
          </LazyLoad>
          <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 18 }}>
            <Button
              type={signatureValid ? "primary" : "default"}
              loading={verifying}
              danger={signatureValid === false}
              onClick={verifySignature}
              size="small"
            >
              {signatureValid === true ? `签名有效 ✓` : signatureValid === false ? "签名无效 ✗" : "验证签名"}
            </Button>
            <Button
              type={liked ? 'primary' : 'default'}
              shape="round"
              icon={<span role="img" aria-label="like">👍</span>}
              onClick={handleLike}
              style={{ background: liked ? '#ffe58f' : undefined, color: liked ? '#faad14' : undefined, border: liked ? '1px solid #faad14' : undefined, fontWeight: 600 }}
            >
              {likes} 赞
            </Button>
          </div>
          <div style={{ marginTop: 40 }}>
            <h3 style={{ marginBottom: 16 }}>评论区</h3>
            <AntList
              dataSource={comments}
              locale={{ emptyText: '暂无评论' }}
              renderItem={item => (
                <SimpleComment
                  author={item.author}
                  avatar={<Avatar style={{ background: '#1677ff' }}>{item.author[0]}</Avatar>}
                  content={item.content}
                  datetime={item.datetime}
                />
              )}
            />
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <Input.TextArea
                value={commentValue}
                onChange={e => setCommentValue(e.target.value)}
                rows={2}
                maxLength={200}
                placeholder="写下你的评论..."
                style={{ flex: 1 }}
              />
              <AntButton type="primary" loading={commentLoading} onClick={handleComment}>
                发布
              </AntButton>
            </div>
          </div>
        </>
      ) : (
        <Text type="danger">未找到该博客</Text>
      )}
    </div>
  );
}

export default PostDetail;
